import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/layout';
import { Plus, Eye, Pencil, Search, X, ClipboardList } from 'lucide-react';
import { UnileverButton } from '../../components/ButtonComponent';
import PlainButton from '../../components/ButtonComponent';
import FunnelSimple from '../../assets/icon/FunnelSimple.svg';
import DataTable from '../../components/DataTable';
import { fetchModelsOnboarding } from '../../service/apiServices';
import { useAzureData } from '../../context/AzureContext';
import './ModelLibrary.css';
import TooltipWrapper from '../../components/TooltipWrapper';
import { CloudProviderFilter } from '../../components/inputField';
import useCloudProvider from '../../hooks/useCloudProvider';

export type ModelRecord = {
  endpoint_group_id: string;
  endpoint_name: string;
  request_id: string;
  model_id: string;
  model_type: string;
  model_provider: string;
  model_version?: string | null;
  deployment_name?: string | null;
  task: string;
  cloud_platform_id?: number;

  // --- Model Specific (Gemini) ---
  google_region?: string | null;
  project_id?: string | null;

  // --- Model Specific (OpenAI) ---
  api_version?: string | null;
  base_url?: string | null;

  // --- Model Specific (Databricks) ---
  openai_configs?: {
    request_id: string;
    api_key_scope: string;
    traffic_percentage: number;
    base_url: string;
    api_version: string;
  }[];

  // --- Model Specific (Databricks) ---
  databricks_configs?: {
    request_id: string;
    workload_size: string;
    workload_type: string;
    min_provisioned_throughput: number;
    max_provisioned_throughput: number;
    traffic_percentage: number;
  }[];

  // --- Model Specific (Gemini) ---
  gemini_configs?: {
    request_id: string;
    google_region: string;
    project_id: string;
    traffic_percentage: number;
  }[];

  // --- Rate Limit ---
  keys?: string | null;
  renewal_period?: string | null;
  calls?: number | null;

  // --- Guardrails ---
  pii_behaviour?: string | null;
  safety?: string | null;
  configuration?: string | null;
  status: string;
};

const ModelLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { apiToken } = useAzureData();
  //
  const { cloudProvidersList, selectedCloud, selectCloud } = useCloudProvider();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<{
    model_type: string;
    model_provider: string;
    task: string;
    status: string;
  }>({
    model_type: '',
    model_provider: '',
    task: '',
    status: ''
  });

  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const [viewModel, setViewModel] = useState<ModelRecord | null>(null);
  const [activeTab, setActiveTab] = useState<'rateLimit' | 'guardrails' | 'modelSpecific'>(
    'modelSpecific'
  );
  const [models, setModels] = useState<ModelRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!apiToken) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchModelsOnboarding(apiToken);
        const mappedModels = response.models.map((model: ModelRecord) => ({
          ...model,
          openai_configs: model.openai_configs ?? [],
          databricks_configs: model.databricks_configs ?? [],
          gemini_configs: model.gemini_configs ?? []
        }));
        setModels(mappedModels);
      } catch (err: any) {
        console.error(err);
        setError(err.message || 'Failed to fetch models');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [apiToken]);

  const hasPending = models?.length > 0 && models?.some((model) => model.status === 'pending');

  // Unique filter values
  const uniqueModelTypes = useMemo(
    () => Array.from(new Set(models.map((m) => m.model_type))),
    [models]
  );
  const uniqueProviders = useMemo(
    () => Array.from(new Set(models.map((m) => m.model_provider))),
    [models]
  );
  const uniqueTasks = useMemo(
    () => Array.from(new Set(models.map((m) => m.task?.replace(/^llm\/v1\//, '') ?? '-'))),
    [models]
  );
  const uniqueStatuses = useMemo(() => Array.from(new Set(models.map((m) => m.status))), [models]);

  // Filter + Search
  const filteredList = useMemo(() => {
    return models.filter((model) => {
      const query = searchQuery.toLowerCase();
      const combined = [
        model.endpoint_group_id,
        model.endpoint_name,
        model.model_type,
        model.model_provider,
        model.model_version ?? '',
        model.deployment_name ?? '',
        model.task
      ]
        .join(' ')
        .toLowerCase();
      if (!combined.includes(query)) return false;

      if (filters.model_type && model.model_type !== filters.model_type) return false;
      if (filters.model_provider && model.model_provider !== filters.model_provider) return false;
      if (filters.task && model.task?.replace(/^llm\/v1\//, '') !== filters.task) return false;
      if (filters.status && model.status !== filters.status) return false;
      if (selectedCloud && model?.cloud_platform_id !== selectedCloud) return false;

      return true;
    });
  }, [searchQuery, filters, models, selectedCloud]);

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setShowFilter(false);
  };

  const handleResetAll = () => {
    setTempFilters({ model_type: '', model_provider: '', task: '', status: '' });
    selectCloud(0);
  };

  const handleReset = (field: string) => {
    setTempFilters({ ...tempFilters, [field]: '' });
  };

  // DataTable headers

  const headers = [
    {
      name: 'Endpoint Name',
      type: 'endpoint_name',
      sortingAllowed: true,
      className: 'col-endpoint-name'
    },
    { name: 'Model Type', type: 'model_type', sortingAllowed: false, className: 'col-model-type' },
    {
      name: 'Cloud Provider',
      type: 'cloud_provider',
      sortingAllowed: false,
      className: 'col-model-type'
    },
    {
      name: 'Model Provider',
      type: 'model_provider',
      sortingAllowed: false,
      className: 'col-model-provider'
    },
    { name: 'Version', type: 'model_version', sortingAllowed: false },
    {
      name: 'Deployment Name',
      type: 'deployment_name',
      sortingAllowed: true,
      className: 'col-deployment-name'
    },
    { name: 'Task', type: 'task', sortingAllowed: false },
    { name: 'Status', type: 'status', sortingAllowed: false },
    { name: 'Actions', type: 'actions', sortingAllowed: false }
  ];

  // Map models to DataTable format
  const tableData = filteredList.map((model) => {
    const getStatusBadge = (status: string | undefined | null) => {
      if (!status) return <span className="status-badge">-</span>;

      let cssClass = status.toLowerCase();

      // map API status → CSS class
      if (cssClass === 'processed') cssClass = 'approved';
      if (cssClass === 'failed') cssClass = 'rejected';
      // pending already matches

      return <span className={`status-badge ${cssClass}`}>{status}</span>;
    };

    return {
      request_id: model.request_id,
      endpoint_group_id: model.endpoint_group_id,
      endpoint_name: model.endpoint_name,
      model_id: model.model_id,
      model_type: model.model_type,
      model_provider: model.model_provider,
      model_version: model.model_version ?? '-',
      deployment_name: model.deployment_name ?? '-',
      task: model.task?.replace(/^llm\/v1\//, '') ?? '-',
      status: getStatusBadge(model.status),
      cloud_provider:
        cloudProvidersList?.filter((itm) => itm?.id === model?.cloud_platform_id)?.[0]?.name ?? '-',
      actions: (
        <div style={{ display: 'flex', gap: '25px' }}>
          <Eye size={18} style={{ cursor: 'pointer' }} onClick={() => setViewModel(model)} />
          {hasPending ? (
            <TooltipWrapper
              id="add-model"
              tooltipText='Button will remain disabled until any Model status is "Pending"'>
              <Pencil size={18} style={{ cursor: 'not-allowed', color: '#A0A0A0' }} />
            </TooltipWrapper>
          ) : (
            <Pencil
              size={18}
              style={{ cursor: 'pointer' }}
              onClick={() =>
                navigate('/modelOnboarding', {
                  state: { editModelId: model.endpoint_group_id, model: model }
                })
              }
            />
          )}
        </div>
      )
    };
  });

  return (
    <div className="p-4">
      {/* Header & Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <PageHeader
          headerName="Model Library"
          description="Browse and manage all registered AI models">
          <ClipboardList size={20} strokeWidth={2} color="#0066CC" />
        </PageHeader>
        <div className="d-flex align-items-center gap-2">
          <div className="search-box d-flex align-items-center">
            <Search size={18} strokeWidth={1.5} color="#838FB4" />
            <input
              type="text"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="filter-container" style={{ position: 'relative' }}>
            <PlainButton buttonAction={() => setShowFilter((prev) => !prev)}>
              <img src={FunnelSimple} alt="Filter" />
              <span style={{ color: '#111', marginLeft: '4px' }}>Filter</span>
            </PlainButton>

            {showFilter && (
              <div
                className="filter-popup"
                style={{
                  top: '100%',
                  right: '-110px',
                  zIndex: 9999
                }}>
                <div className="filter-header">
                  Filter
                  <button className="close-btn" onClick={() => setShowFilter(false)}>
                    <X size={20} strokeWidth={2.5} color="#838FB4" />
                  </button>
                </div>
                <div className="filter-section">
                  <label>Model Type</label>
                  <button
                    className="reset-link"
                    onClick={() => {
                      handleReset('model_type');
                    }}>
                    Reset
                  </button>

                  <select
                    value={tempFilters.model_type}
                    onChange={(e) =>
                      setTempFilters((prev) => ({ ...prev, model_type: e.target.value }))
                    }>
                    <option value="">Select</option>
                    {uniqueModelTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-section">
                  <label>Model Provider</label>
                  <button
                    className="reset-link"
                    onClick={() => {
                      handleReset('model_provider');
                    }}>
                    Reset
                  </button>

                  <select
                    value={tempFilters.model_provider}
                    onChange={(e) =>
                      setTempFilters((prev) => ({ ...prev, model_provider: e.target.value }))
                    }>
                    <option value="">Select</option>
                    {uniqueProviders.map((provider) => (
                      <option key={provider} value={provider}>
                        {provider}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Cloud Provider */}
                <CloudProviderFilter
                  cloudProvidersList={cloudProvidersList}
                  selectedCloud={selectedCloud}
                  selectCloud={selectCloud}
                />
                <div className="filter-section">
                  <label>Task</label>
                  <button
                    className="reset-link"
                    onClick={() => {
                      handleReset('task');
                    }}>
                    Reset
                  </button>
                  <select
                    value={tempFilters.task}
                    onChange={(e) => setTempFilters((prev) => ({ ...prev, task: e.target.value }))}>
                    <option value="">Select</option>
                    {uniqueTasks.map((task) => (
                      <option key={task} value={task}>
                        {task}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-section">
                  <label>Status</label>
                  <button
                    className="reset-link"
                    onClick={() => {
                      handleReset('status');
                    }}>
                    Reset
                  </button>
                  <select
                    value={tempFilters.status}
                    onChange={(e) =>
                      setTempFilters((prev) => ({ ...prev, status: e.target.value }))
                    }>
                    <option value="">Select</option>
                    {uniqueStatuses.map((status) => {
                      const label = status.charAt(0).toUpperCase() + status.slice(1); // Capitalize first letter
                      return (
                        <option key={status} value={status}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>
                <div className="filter-footer">
                  <button className="reset-all" onClick={handleResetAll}>
                    Reset All
                  </button>
                  <button className="apply-btn" onClick={handleApplyFilters}>
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {hasPending ? (
            <TooltipWrapper
              id="add-model"
              tooltipText='Button will remain disabled until any Model status is "Pending"'>
              <UnileverButton
                disabled={hasPending}
                buttonAction={() => navigate('/modelOnboarding')}>
                <Plus size={16} /> Add Model
              </UnileverButton>
            </TooltipWrapper>
          ) : (
            <UnileverButton disabled={hasPending} buttonAction={() => navigate('/modelOnboarding')}>
              <Plus size={16} /> Add Model
            </UnileverButton>
          )}
        </div>
      </div>

      {/* DataTable */}
      <div>
        <DataTable itemOptions={[10, 20, 30, 50]} header={headers} data={tableData} />
      </div>

      {/* View Model Modal */}
      {viewModel && (
        <div className="side-modal-overlay">
          <div className="side-modal">
            <div className="side-modal-header">
              <div>
                <h2 className="app-title">{viewModel.endpoint_name}</h2>
                <p className="app-subtitle">Model details</p>
              </div>
              <button className="modal-close" onClick={() => setViewModel(null)}>
                <X size={20} strokeWidth={2.5} color="#838FB4" />
              </button>
            </div>

            <div className="side-modal-body">
              <div className="info-row">
                <span className="label">Model Provider</span>
                <span>{viewModel.model_provider ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Model Type</span>
                <span>{viewModel.model_type ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Task</span>
                <span>{viewModel.task?.replace(/^llm\/v1\//, '') ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Deployment Name</span>
                <span>{viewModel.deployment_name ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Model Version</span>
                <span>{viewModel.model_version ?? '-'}</span>
              </div>
              <div className="info-row">
                <span className="label">Endpoint Name</span>
                <span>{viewModel.endpoint_name ?? '-'}</span>
              </div>

              {/* --- Tabs Section --- */}
              <div className="tabs">
                <button
                  className={`tab ${activeTab === 'modelSpecific' ? 'active' : ''}`}
                  style={{
                    fontSize: '14px',
                    color: activeTab === 'modelSpecific' ? '#111111' : '#838FB4'
                  }}
                  onClick={() => setActiveTab('modelSpecific')}>
                  Model Specific{viewModel?.model_type ? ` (${viewModel.model_type})` : ''}
                </button>
                <button
                  className={`tab ${activeTab === 'rateLimit' ? 'active' : ''}`}
                  style={{
                    fontSize: '14px',
                    color: activeTab === 'rateLimit' ? '#111111' : '#838FB4'
                  }}
                  onClick={() => setActiveTab('rateLimit')}>
                  Rate Limit
                </button>
                <button
                  className={`tab ${activeTab === 'guardrails' ? 'active' : ''}`}
                  style={{
                    fontSize: '14px',
                    color: activeTab === 'guardrails' ? '#111111' : '#838FB4'
                  }}
                  onClick={() => setActiveTab('guardrails')}>
                  Guardrails
                </button>
              </div>

              <div className="tab-content">
                {activeTab === 'modelSpecific' && (
                  <>
                    {viewModel.model_type === 'Gemini' &&
                      viewModel.gemini_configs &&
                      viewModel.gemini_configs.length > 0 && (
                        <div className="config-cards-container">
                          {viewModel.gemini_configs.map((config) => (
                            <div key={config.request_id} className="config-card">
                              <div className="card-row">
                                <span className="label">Request ID:</span> {config.request_id}
                              </div>
                              <div className="card-row">
                                <span className="label">Google Region:</span> {config.google_region}
                              </div>
                              <div className="card-row">
                                <span className="label">Project ID:</span> {config.project_id}
                              </div>
                              <div className="card-row">
                                <span className="label">Traffic %:</span>{' '}
                                {config.traffic_percentage}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                    {viewModel.model_type === 'OpenAI' && viewModel.openai_configs && (
                      <div className="config-cards-container">
                        {viewModel.openai_configs.map((config, idx) => (
                          <div key={config.request_id} className="config-card">
                            <div className="card-row">
                              <span className="label">Request ID:</span> {config.request_id}
                            </div>
                            <div className="card-row">
                              <span className="label">API Key Scope:</span> {config.api_key_scope}
                            </div>
                            <div className="card-row">
                              <span className="label">Traffic %:</span> {config.traffic_percentage}
                            </div>
                            <div className="card-row">
                              <span className="label">Base URL:</span> {config.base_url}
                            </div>
                            <div className="card-row">
                              <span className="label">API Version:</span> {config.api_version}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {viewModel.model_type === 'Databricks' &&
                      viewModel.databricks_configs &&
                      viewModel.databricks_configs.length > 0 && (
                        <div className="config-cards-container">
                          {viewModel.databricks_configs.map((config) => (
                            <div key={config.request_id} className="config-card">
                              <div className="card-row">
                                <span className="label">Request ID:</span> {config.request_id}
                              </div>
                              <div className="card-row">
                                <span className="label">Workload Size:</span> {config.workload_size}
                              </div>
                              <div className="card-row">
                                <span className="label">Workload Type:</span> {config.workload_type}
                              </div>
                              <div className="card-row">
                                <span className="label">Min Provisioned Throughput:</span>{' '}
                                {config.min_provisioned_throughput}
                              </div>
                              <div className="card-row">
                                <span className="label">Max Provisioned Throughput:</span>{' '}
                                {config.max_provisioned_throughput}
                              </div>
                              <div className="card-row">
                                <span className="label">Traffic %:</span>{' '}
                                {config.traffic_percentage}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </>
                )}

                {activeTab === 'rateLimit' && (
                  <>
                    <div className="info-row">
                      <span className="label">Rate Limit Key</span>
                      <span>{viewModel.keys ?? '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Renewal Period</span>
                      <span>{viewModel.renewal_period ?? '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Calls</span>
                      <span>{viewModel.calls ?? '-'}</span>
                    </div>
                  </>
                )}

                {activeTab === 'guardrails' && (
                  <>
                    <div className="info-row">
                      <span className="label">PII Behaviour</span>
                      <span>{viewModel.pii_behaviour ?? '-'}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Safety</span>
                      <span>{viewModel.safety ?? '-'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModelLibrary;
